import React, { useEffect, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useNavigate, useLocation } from "react-router-dom";
import { Typography, Box } from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_MEMBER_DETAILS } from "../graphql/queries";
import ReceiptPreview from "./ReceiptPreview";
import NotLoggedIn from "../components/LoginExceptions";

const PaymentReceipt = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const passedMember = location.state?.user;
  const [mobile, setMobile] = useState(passedMember?.Mobile_Number || "");
  const storedUserType = localStorage.getItem("user_type");
  window.scrollTo(0, 0);

  const { loading, error, data, refetch } = useQuery(GET_MEMBER_DETAILS, {
    variables: { mobile },
    skip: !mobile || !!passedMember, // Skip query if passed member is available
    fetchPolicy: "network-only", // Always gets fresh data
  });
  
  useEffect(() => {
    const storedMobile = localStorage.getItem("mobile");
    if (storedMobile && !passedMember) {
      setMobile(storedMobile);
    }
  }, [passedMember]);
  
  useEffect(() => {
    if (mobile && !passedMember) {
      refetch(); // Auto refresh when component mounts or mobile is set
    }
  }, [mobile, refetch, passedMember]);  

  if (!storedUserType) {
    return <NotLoggedIn />;
  }

  const member = passedMember || data?.member;

  // // Display user info if passed from another component
  // const displayUserInfo = passedMember && (
  //   <Box sx={{ 
  //     mb: 3, 
  //     p: 2, 
  //     backgroundColor: "#fff", 
  //     borderRadius: 2, 
  //     boxShadow: 1,
  //     maxWidth: 600,
  //     width: "100%"
  //   }}>
  //     <Typography variant="h6" sx={{ color: "#2A289C", mb: 1 }}>
  //       Member Information
  //     </Typography>
  //     <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
  //       {passedMember.First_Name && (
  //         <Typography variant="body2">
  //           <strong>Name:</strong> {passedMember.First_Name} {passedMember.Last_Name || ''}
  //         </Typography>
  //       )}
  //       {passedMember.Member_ID && (
  //         <Typography variant="body2">
  //           <strong>Member ID:</strong> {passedMember.Member_ID}
  //         </Typography>
  //       )}
  //       {passedMember.District && (
  //         <Typography variant="body2">
  //           <strong>District:</strong> {passedMember.District}
  //         </Typography>
  //       )}
  //       {passedMember.Location && (
  //         <Typography variant="body2">
  //           <strong>Location:</strong> {passedMember.Location}
  //         </Typography>
  //       )}
  //       {passedMember.Mobile_Number && (
  //         <Typography variant="body2">
  //           <strong>Mobile:</strong> {passedMember.Mobile_Number}
  //         </Typography>
  //       )}
  //       {passedMember.Email && (
  //         <Typography variant="body2">
  //           <strong>Email:</strong> {passedMember.Email}
  //         </Typography>
  //       )}
  //     </Box>
  //   </Box>
  // );

  if (loading)
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-grow flex justify-center items-center">
          <p className="text-gray-600">Loading...</p>
        </div>
        <footer className="bg-gray-200 text-center py-4 text-sm text-gray-600">
          © 2025 Your Organization. All rights reserved.
        </footer>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-grow flex justify-center items-center">
          <p className="text-red-600">Error loading member details.</p>
        </div>
        <footer className="bg-gray-200 text-center py-4 text-sm text-gray-600">
          © 2025 Your Organization. All rights reserved.
        </footer>
      </div>
    );

  if (!member)
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-grow flex justify-center items-center">
          <p className="text-gray-600">No member data available.</p>
        </div>
        <footer className="bg-gray-200 text-center py-4 text-sm text-gray-600">
          © 2025 Your Organization. All rights reserved.
        </footer>
      </div>
    );

  const isPaymentPaid = member.Payment_Status === "Paid";

  const downloadPdf = () => {
    // Always use the hidden receipt-for-pdf element which has consistent formatting
    const input = document.getElementById("receipt-for-pdf");
    if (!input) return;

    html2canvas(input, {
      scale: 2, // Higher scale for better quality
      useCORS: true,
      logging: false,
      // Force consistent width to match desktop appearance
      width: 794, // A4 width at 96dpi
      height: 1123, // A4 height at 96dpi
      windowWidth: 794,
      windowHeight: 1123,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      
      // Get PDF dimensions
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Add image to fill the page
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      
      // Use member name in filename if available
      const fileName = member?.First_Name ? 
        `${member.First_Name}_receipt.pdf` : 
        "payment_receipt.pdf";
      
      pdf.save(fileName);
    });
  };

  return (
    <div
      style={{ backgroundColor: '#f8f9e9' }}
      className="max-w-4xl w-full mx-auto border p-6 shadow-lg min-h-[500px] flex flex-col items-center justify-center"
    >
      {/* Display passed user information */}
      {/* {displayUserInfo} */}

      {!isPaymentPaid ? (
        <Typography variant="body1" color="#7A7841" textAlign="center">
          To view or download the receipt, please complete your pending payment.{" "}
          <Typography
            component="span"
            sx={{ cursor: "pointer", textDecoration: "underline", color: "blue" }}
            onClick={() => {
              navigate("/paymentgateway");
              window.scrollTo(0, 0);
            }}
          >
            Click here
          </Typography>{" "}
          to proceed to the payment process. Once the payment is successful, the receipt will be available.
        </Typography>
      ) : (
        <>
          {/* Visible receipt for display purpose */}
          <div className="w-full">
            <ReceiptPreview member={member} />
          </div>

          {/* Hidden fixed-width receipt only for PDF export - always uses desktop layout */}
          <div
            style={{
              position: "absolute",
              left: "-9999px",
              top: 0,
              width: "794px", // A4 width at 96dpi
              height: "1123px", // A4 height at 96dpi
              overflow: "hidden",
              backgroundColor: "white",
            }}
          >
            <div
              id="receipt-for-pdf"
              className="w-full h-full bg-white"
            >
              <ReceiptPreview 
                member={member} 
                forDownload={true} 
              />
            </div>
          </div>

          <div className="flex justify-center mt-6">
            <button
              onClick={downloadPdf}
              style={{ backgroundColor: '#2A289C', borderRadius: '999px' }}
              className="text-white px-4 py-2 rounded"
            >
              Download Receipt
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentReceipt;